import { useEffect } from 'react';
import { useSteleStore } from '@/state/store';
import {
  IdleOrb, IdleContent, Calling, InCall,
  Waiting, Unavailable, Feedback, Goodbye,
} from '@/views/SteleViews';
import { StateHarness } from '@/components/StateHarness';
import { VersionPill } from '@/components/VersionPill';
import { SignatureOverlay } from '@/components/SignaturePad';

export default function App() {
  const view = useSteleStore((s) => s.view);
  const setSteleId = useSteleStore((s) => s.setSteleId);
  const setIdentity = useSteleStore((s) => s.setIdentity);

  useEffect(() => {
    // Stele-ID aus URL-Query (?stele=xxx) oder localStorage
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('stele');
    if (fromUrl) {
      setSteleId(fromUrl);
      localStorage.setItem('bamoa_stele_id', fromUrl);
    } else {
      const stored = localStorage.getItem('bamoa_stele_id');
      if (stored) setSteleId(stored);
    }
  }, [setSteleId]);

  // Welle 9: Echte Stelen-Identität laden (Name, Standort, Mandant) — ersetzt i18n-Platzhalter.
  const steleId = useSteleStore((s) => s.steleId);
  useEffect(() => {
    if (!steleId) return;
    let alive = true;
    fetch(`/api/steles/${steleId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => {
        if (alive && s) setIdentity({ steleName: s.name, tenantName: s.tenant?.name ?? '', location: s.location ?? null, logo: s.tenant?.logo ?? null });
      })
      .catch(() => { /* offline → i18n-Fallback greift */ });
    return () => { alive = false; };
  }, [steleId, setIdentity]);

  // pre186: Push statt Polling — Verfügbarkeit per SSE-Stream (near-instant).
  const refreshAvailability = useSteleStore((s) => s.refreshAvailability);
  const setAvailability = useSteleStore((s) => s.setAvailability);
  useEffect(() => {
    if (!steleId) return;
    void refreshAvailability(); // initialer Wert + Fallback, falls SSE nicht greift
    let es: EventSource | null = null;
    let fallback: ReturnType<typeof setInterval> | null = null;
    const startFallback = () => { if (!fallback) fallback = setInterval(() => { void refreshAvailability(); }, 20000); };
    try {
      es = new EventSource(`/api/calls/availability/${steleId}/stream`);
      es.onmessage = (ev) => {
        if (fallback) { clearInterval(fallback); fallback = null; } // SSE liefert → kein Poll nötig
        try {
          const j = JSON.parse(ev.data);
          setAvailability(
            typeof j?.available === 'boolean' ? j.available : null,
            (typeof j?.notice === 'string' && j.notice.trim()) ? j.notice : null,
          );
        } catch { /* */ }
      };
      es.onerror = () => { startFallback(); }; // EventSource reconnectet selbst; Fallback nur als Netz
    } catch { startFallback(); }
    return () => { es?.close(); if (fallback) clearInterval(fallback); };
  }, [steleId, refreshAvailability, setAvailability]);

  return (
    <>
      <StateHarness />
      <div className="stage">
        <div className="stele" id="stele">
          {view === 'idle-orb' && <IdleOrb />}
          {view === 'idle-content' && <IdleContent />}
          {view === 'calling' && <Calling />}
          {view === 'incall' && <InCall />}
          {view === 'waiting' && <Waiting />}
          {view === 'unavailable' && <Unavailable />}
          {view === 'feedback' && <Feedback />}
          {view === 'goodbye' && <Goodbye />}
        </div>
      </div>
      <VersionPill />
      <SignatureOverlay />
    </>
  );
}
